import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../../DataContext";
import "./product-page2.css";
import DateRangePicker from "../date-range-picker/date-range-picker.js";
import FooterContent from "../common/footer-content/footer-content.js";
import HeaderContent from "../common/header-content/header-content.js";
import CountryDropDown from "../common/country-dropdown/country-dropdown.js";
import "bootstrap/dist/css/bootstrap.min.css";
import TermsPopup from "../common/dhl-terms-conditions-popup/dhl-terms-conditions-popup.js";

const ProductPage2 = ({ client, previousClient, setPreviousClient }) => {
  const [shipperReference, setShipperReference] = useState("");
  const [airwaybillNumber, setAirwaybillNumber] = useState("");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const navigate = useNavigate();
  const { setTrackingParameters } = useData();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showTooltipForAwb, setShowTooltipForAwb] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [originValue, setOriginValue] = useState("");
  const [destinationValue, setDestinationValue] = useState("");

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 14);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Set the initial date values when the component mounts
  useEffect(() => {
    setStartDate(yesterday);
    setEndDate(today);
  }, []);

  useEffect(() => {
    if (previousClient === client.customerCode) {
      // If the previous client is the same, retain the values
      const storedShipperReference = sessionStorage.getItem("shipperReference");
      const storedAirwaybillNumber = sessionStorage.getItem("airwaybillNumber");
      const storedOrigin = sessionStorage.getItem("origin");
      const storedDestination = sessionStorage.getItem("destination");
      const storedStartDate = sessionStorage.getItem("startDate");
      const storedEndDate = sessionStorage.getItem("endDate");
      const storedOriginValue = sessionStorage.getItem("originValue");
      const storedDestinationValue = sessionStorage.getItem("destinationValue");

      if (storedShipperReference) {
        setShipperReference(storedShipperReference);
      }
      if (storedStartDate) {
        setStartDate(new Date(storedStartDate.split("/").reverse().join("-")));
      }
      if (storedEndDate) {
        setEndDate(new Date(storedEndDate.split("/").reverse().join("-")));
      }
      if (storedAirwaybillNumber) {
        setAirwaybillNumber(storedAirwaybillNumber);
      }
      if (storedOrigin) {
        setOrigin(storedOrigin);
      }
      if (storedDestination) {
        setDestination(storedDestination);
      }
      if(storedOriginValue){
        setOriginValue(storedOriginValue);
      }
      if(storedDestinationValue){
        setDestinationValue(storedDestinationValue);
      }
    } else {
      // If the previous client is different, clear the values
      setShipperReference("");
      setAirwaybillNumber("");
      setOrigin("");
      setDestination("");
      setStartDate(yesterday); // Set to yesterday
      setEndDate(today); // Set to today
      setOriginValue("");
      setDestinationValue("");
      sessionStorage.clear(); // Clear session storage
    }
  }, [previousClient, client.customerCode]);

  const formatDate = (date) => {
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${day}/${month}/${year}`;
    }
    return "";
  };

  const checkVariablesPresence = (...variables) => {
    return variables.some((variable) => variable !== null && variable !== undefined && variable !== "");
  };

  const handleTrack = useCallback(
    (isShipperReference) => {
      const trackingParams = {
        sref: isShipperReference
          ? shipperReference
              .split(/[,]+/)
              .map((item) => item.trim())
              .filter((item) => item)
              .join(",")
          : "",
        awb: airwaybillNumber
          .split(/[,]+/)
          .map((item) => item.trim())
          .filter((item) => item)
          .join(","),
        fromDate: startDate ? formatDate(startDate) : null,
        toDate: endDate ? formatDate(endDate) : null,
        locale: "en",
        custcode: client.customerCode,
        origin: originValue,
        destination: destinationValue,
      };

      // Store values in sessionStorage before navigating
      sessionStorage.setItem("shipperReference", shipperReference);
      sessionStorage.setItem("airwaybillNumber", airwaybillNumber);
      sessionStorage.setItem("origin", origin);
      sessionStorage.setItem("destination", destination);
      sessionStorage.setItem("startDate", formatDate(startDate));
      sessionStorage.setItem("endDate", formatDate(endDate));
      sessionStorage.setItem("originValue", originValue);
      sessionStorage.setItem("destinationValue", destinationValue);
      setPreviousClient(client.customerCode);
      setTrackingParameters(trackingParams);
      //navigate(`/mrt?type=${isShipperReference ? 0 : 1}`);
      navigate("/mrt", { state: { source: 'client' } });
    },
    [
      shipperReference,
      airwaybillNumber,
      startDate,
      endDate,
      client.customerCode,
      navigate,
      setTrackingParameters,
      origin,
      destination,
      setPreviousClient,
    ]
  );

  const handleClear = useCallback((type) => {
    if (type === "shipperReference") {
      setShipperReference("");
      setOrigin("");
      setDestination("");
      setStartDate(null);
      setEndDate(null);
      setOriginValue("");
      setDestinationValue("");

      // Clear values from sessionStorage
      sessionStorage.removeItem("shipperReference");
      sessionStorage.removeItem("origin");
      sessionStorage.removeItem("destination");
      sessionStorage.removeItem("startDate");
      sessionStorage.removeItem("endDate");
      sessionStorage.removeItem("originValue");
      sessionStorage.removeItem("destinationValue");
    } else if (type === "airwaybillNumber") {
      setAirwaybillNumber("");
      sessionStorage.removeItem("airwaybillNumber");
    }
  }, []);

  const showOrigin = client.origin; // Set to true to show the origin select
  const showDestination = client.destination;

  const handleSelectionChange = (newOrigin, newDestination) => {
    if (newOrigin === null) {
      setOrigin(""); // Clear origin state
      setOriginValue("");
    } else {
      if (newOrigin && typeof newOrigin === "object" && "label" in newOrigin && "value" in newOrigin) {
        setOrigin(newOrigin.label);
        setOriginValue(newOrigin.value);
      }
    }
   
    if (newDestination === null) {
      setDestination(""); // Clear destination state
      setDestinationValue("");
    } else {
      if (
        newDestination &&
        typeof newDestination === "object" && "label" in newDestination &&
        "value" in newDestination
      ) {
        setDestination(newDestination.label);
        setDestinationValue(newDestination.value);
      }    
    }
  };
  
  const handleChange = (e, field) => {
    const input = e.target.value;
    const commaCount = (input.match(/,/g) || []).length;
    if (commaCount < 9 || commaCount === 9) {
      if (field === "shipperReference") {
        setShipperReference(input);
      } else if (field === "airwaybillNumber") {
        setAirwaybillNumber(input);
      }
    }
  };

  const handleTermsAccept = (accepted) => {
    setIsTermsAccepted(accepted); // Set the checkbox state to true
  };

    // Check if all mandatory fields are filled
    const isAllFieldsFilled =
    shipperReference.trim() !== "" &&
    startDate &&
    endDate;

  const tooltipMessage = () => {
    if (!isAllFieldsFilled) {
      return "All fields marked with an asterisk (*) are mandatory.";
    }
    if (isAllFieldsFilled && !isTermsAccepted) {
      return "Please accept the terms and conditions.";
    }
    return "";
  };

  const tooltipMessageForAwb = () => {
    if (!airwaybillNumber) {
      return "Airwaybill number is mandatory.";
    }
    if (airwaybillNumber && !isTermsAccepted) {
      return "Please accept the terms and conditions.";
    }
    return "";
  };

  const isShipperReferenceValid = isAllFieldsFilled && isTermsAccepted; 

  const isShipperReferenceClearEnabled =
    shipperReference.trim() !== "" || startDate || endDate || origin || destination;

  const isAirwayBillValid = airwaybillNumber.trim() !== "" &&  isTermsAccepted;
  const isAirwayBillClearEnabled = airwaybillNumber.trim() !== "";

  const isClientDetailsConfigured = checkVariablesPresence(
    client.line1,
    client.line2,
    client.line3,
    client.line4,
    client.textAboveShipRefSearchLine1,
    client.textAboveShipRefSearchLine2,
    client.shipRefSearchText,
    client.textAboveAwbSearch,
    client.awbText,
    client.awbSearchText,
    client.textBelowAwbSearchInRed,
    client.textBelowAwbSearchInBlack,
    client.textForDhlTermsAndConditions,
    client.originLabel
  );

  const originLabel =
    isClientDetailsConfigured && client.originLabel
      ? client.originLabel + ": "
      : "Origin: ";

   const parseDisclaimer = (text) => {
      // Check if there are any <b> tags in the text
      if (!text.includes("<b>")) {
        return <p>{text}</p>; // Return the text as-is if no <b> tags are present
      }
  
      const regex = /<b>(.*?)<\/b>/g; // Regular expression to match <b>...</b>
      const parts = text.split(regex); // Split the text based on the regex
  
      return parts.map((part, index) => {
        // Check if the current part is bold text
        if (index % 2 === 1) {
          return (
            <React.Fragment key={index}>
              <strong style={{ fontStyle: "italic" }}>{part}</strong>{" "}
              {/* Render as bold and italic */}
              <br /> {/* Add a line break after bold text */}
            </React.Fragment>
          );
        } else if (part.trim()) {
          // Render normal text in italic if it's not empty
          return (
            <React.Fragment key={index}>
              <em>{part}</em> {/* Render normal text in italic */}
              {index < parts.length - 1 && <br />}{" "}
              {/* Add a line break if not the last part */}
            </React.Fragment>
          );
        }
        return null;
      });
    };

  return (
    <div className="outer-body-container text-02">
      <HeaderContent
        toDisplayClientLogo={true}
        client={client}
        currentPage="clientPage"
        locale="en"
        countryCode="gb"
      />
      <TermsPopup 
        isOpen={isTermsModalOpen} 
        onClose={() => setIsTermsModalOpen(false)} 
        onAccept={handleTermsAccept} // Pass the accept handler
      />
      <div className="body-content">
        {/* <div className="column-left"></div> */}
        <div className="box column-middle">
          <div className="contentbody">
            {isClientDetailsConfigured === true ? (
              <div>
                {client.line1 && (
                  <h1 className="text-01 color-red bold-text">
                    {client.line1}
                  </h1>
                )}

                {client.line2 && <p className="top-gap">{client.line2}</p>}

                {client.line3 && <p>{client.line3}</p>}

                {client.line4 && <p>{parseDisclaimer(client.line4)}</p>}

                {client.textAboveShipRefSearchLine1 && (
                  <span className="bold-text italiac-format top-gap">
                    {client.textAboveShipRefSearchLine1}
                  </span>
                )}

                {client.textAboveShipRefSearchLine2 && (
                  <p className="bold-text">
                    {client.textAboveShipRefSearchLine2}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <h1 className="text-01 color-red bold-text">
                  Welcome to the DHL and {client.shipRefName} Shipment Tracking
                  System.
                </h1>
                <p className="top-gap">
                  Track your trade documents here 24 hours a day, 7 days a week,
                  wherever you are.
                </p>
                <p>
                  Simply enter your {client.shipRefName} Number OR 10-digit DHL
                  Airwaybill (AWB) Number below and click on the 'Track' button.
                </p>
              </div>
            )}
            
            <div className="custom-checkbox">
              <input
                type="checkbox"
                className="form-check-input"
                id="termsCheckbox"
                checked={isTermsAccepted}
                onChange={(e) => setIsTermsAccepted(e.target.checked)}
                aria-label="DHL Terms and Condition checkBox"
              />
              <label
                className="form-check-label color-red bold-text"
                //htmlFor="termsCheckbox"
              >
                <span>I hereby confirm that I have read the{" "}</span>
                <a
                  className="terms-color"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault(); // Prevent default anchor behavior
                    setIsTermsModalOpen(true); // Open the modal
                  }}
                >
                  DHL Express Online Services Terms of Use
                </a>
                <span>{" "}and accept the content thereof.</span>
              </label>
            </div>
            <br/>
            <div>
              <form>
                <div className="row">
                  <div className={client.isSrefAwbSplitNeed.toLowerCase() === "y" ? 'col-md-6 mb-3' : 'col-md-6 mb-3'}>
                    <label
                      htmlFor="shipper-reference"
                      className="bold-text label-margin"
                    >
                      {client.shipRefName + ": "}
                    </label>
                    <div
                      className="d-flex align-items-center"
                      style={{ paddingRight: "0px !important" }}
                    >
                      <textarea
                        className="form-control text-area-vertical"
                        id="shipper-reference"
                        value={shipperReference}
                        placeholder="Enter up to 10 numbers, separated by comma"
                        onChange={(e) => handleChange(e, "shipperReference")}
                      />
                      <span className="mandatory-asterisk asterisk-top">*</span>
                    </div>

                    {/* <input
                      className="text-area form-control"
                      type="text"
                      id="shipper-reference"
                      value={shipperReference}
                      onChange={(e) => setShipperReference(e.target.value)}
                    /> */}
                    {(client.origin.toLowerCase() === "y" ||
                      client.destination.toLowerCase() === "y") && (
                      <CountryDropDown
                        originLabel={originLabel}
                        showOrigin={showOrigin}
                        showDestination={showDestination}
                        onChange={handleSelectionChange}
                        origin={origin}
                        destination={destination}
                      />
                    )}
                    <label className="bold-text mt-4">Date Range:</label>
                    <div style={{ paddingLeft: "4px", marginTop: "3px" }}>
                      <DateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={setStartDate}
                        onEndDateChange={setEndDate}
                      />
                    </div>

                    <div className="button-container mt-2">
                      <div
                        onMouseEnter={() =>
                          !isShipperReferenceValid && setShowTooltip(true)
                        }
                        onMouseLeave={() => setShowTooltip(false)}
                      >
                        <button
                          className="button-66"
                          onClick={() => handleTrack(true)}
                          type="button"
                          disabled={!isShipperReferenceValid}
                        >
                          {isClientDetailsConfigured === true &&
                          client.shipRefSearchText
                            ? client.shipRefSearchText
                            : `Track ${client.shipRefName}`}
                        </button>
                      </div>
                      {!isShipperReferenceValid && showTooltip && (
                        <div className="tooltip-message template2-sref-tooltip">
                          {tooltipMessage()}
                        </div>
                      )}
                      <button
                        className="button-66"
                        onClick={() => handleClear("shipperReference")}
                        type="button"
                        disabled={!isShipperReferenceClearEnabled}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className={client.isSrefAwbSplitNeed.toLowerCase() === "y" ? 'col-md-1 or-container' : 'or-container-none'}>
                        <div class="line"></div>
                        <span class="or-text">OR</span>
                        <div class="line"></div>
                  </div>
                  <div className={client.isSrefAwbSplitNeed.toLowerCase() === "y" ? 'col-md-5 mb-3' : 'col-md-6 mb-3'}>
                    <label
                      htmlFor="airwaybill-number"
                      className="bold-text label-margin"
                    >
                      {isClientDetailsConfigured && client.awbText
                        ? client.awbText + ": "
                        : "DHL AirwayBill Number: "}
                    </label>
                    <div className="d-flex align-items-center"
                      style={{ paddingRight: "0px !important" }}
                    >
                      <textarea
                        className="form-control text-area-vertical"
                        id="airwaybill-number"
                        value={airwaybillNumber}
                        placeholder="Enter up to 10 numbers, separated by comma"
                        onChange={(e) => handleChange(e, "airwaybillNumber")}
                      />
                      <span className="mandatory-asterisk asterisk-top">*</span>
                    </div>
                    {/* <input
                      className="text-area form-control"
                      type="text"
                      id="airwaybill-number"
                      value={airwaybillNumber}
                      onChange={(e) => setAirwaybillNumber(e.target.value)}
                    /> */}
                    <div className="button-container mt-2">
                      <div
                        onMouseEnter={() =>
                          !isAirwayBillValid && setShowTooltipForAwb(true)
                        }
                        onMouseLeave={() => setShowTooltipForAwb(false)}
                      >
                        <button
                          className="button-66"
                          onClick={() => handleTrack(false)}
                          type="button"
                          disabled={!isAirwayBillValid}
                        >
                          {isClientDetailsConfigured === true &&
                          client.awbSearchText
                            ? client.awbSearchText
                            : "Track Package"}
                        </button>
                      </div>
                      {!isAirwayBillValid && showTooltipForAwb && (
                        <div className="tooltip-message template2-awb-tooltip">
                          {tooltipMessageForAwb()}
                        </div>
                      )}
                      <button
                        className="button-66"
                        onClick={() => handleClear("airwaybillNumber")}
                        type="button"
                        disabled={!isAirwayBillClearEnabled}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {client.textBelowAwbSearchInRed && (
              <div>
                <br />
                <p className="color-red">{client.textBelowAwbSearchInRed}</p>
              </div>
            )}

            {client.textBelowAwbSearchInBlack && (
              <div>
                <p className="color-grey">{client.textBelowAwbSearchInBlack}</p>
              </div>
            )}
            
              {isClientDetailsConfigured && client.textBelowAwbSearch && (
                <div>
                  <br />
                  <p className="color-red">{client.textBelowAwbSearch}</p>
                </div>
              )}
              <br />
              {/* <div className="top-gap">
              <span>
                {isClientDetailsConfigured ? (
                  client.textForDhlTermsAndConditions && (
                    <span>
                      {client.textForDhlTermsAndConditions + " "}
                      <a
                        className="terms-color"
                        href="https://www.dhl.com/gb-en/home/footer/terms-of-use.html"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        DHL terms and conditions
                      </a>
                    </span>
                  )
                ) : (
                  <span>
                    By completing the form and clicking the "Start Tracking"
                    button, you agree to the standard{" "}
                    <a
                      className="terms-color"
                      href="https://www.dhl.com/gb-en/home/footer/terms-of-use.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      DHL terms and conditions
                    </a>
                  </span>
                )}
              </span>
            </div> */}
            </div>
          </div>
        </div>
        {/* <div className="column-right"></div> */}
      </div>
      <FooterContent />
    </div>
  );
};

export default ProductPage2;
